import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Loader2, UserPlus, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useDebouncedValue } from "../lib/useDebouncedValue";
import { ClearableInput } from "../components/common/ClearableInput";
import { SearchableSelect } from "../components/common/SearchableSelect";
import { useWorkspace } from "../context/WorkspaceContext";
import { PageHeader } from "../components/common/PageHeader";
import { ProjectScopeBanner } from "../components/common/ProjectScopeBanner";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Allocation() {
  const { ctx, dataVersion } = useWorkspace();
  const projectId = ctx.project?.id || "";
  const [types, setTypes] = useState([]);
  const [fieldUsers, setFieldUsers] = useState([]);
  const [typeId, setTypeId] = useState("__all__");
  const [search, setSearch] = useState("");
  const [serialFrom, setSerialFrom] = useState("");
  const [serialTo, setSerialTo] = useState("");
  const [sort, setSort] = useState("serial_asc");
  const [items, setItems] = useState([]); const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1); const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(new Set()); const [open, setOpen] = useState(false); const [fuId, setFuId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [capacity, setCapacity] = useState([]);
  const dSearch = useDebouncedValue(search);
  const dSerialFrom = useDebouncedValue(serialFrom);
  const dSerialTo = useDebouncedValue(serialTo);
  const loadSeq = useRef(0);
  const pageSize = 100;

  // Load the selected field user's current per-type load once the allocate dialog opens.
  useEffect(() => {
    if (!open || !fuId || !projectId) { setCapacity([]); return; }
    api.get("/allocation/field-capacity", { params: { project_id: projectId, field_user_id: fuId } })
      .then((r) => setCapacity(r.data.items || [])).catch(() => setCapacity([]));
  }, [open, fuId, projectId]);

  useEffect(() => {
    api.get("/material-types", { params: { page_size: 500 } }).then((r) => setTypes(r.data.items || []));
  }, [dataVersion]);

  // Field users assigned to the active project. Uses the allocation-scoped endpoint
  // (gated by MATERIAL.ALLOCATE) so allocators without USER.VIEW can still load the list.
  useEffect(() => {
    if (!projectId) { setFieldUsers([]); return; }
    api.get("/allocation/field-users", { params: { project_id: projectId } })
      .then((r) => setFieldUsers(r.data.items || [])).catch(() => setFieldUsers([]));
  }, [projectId, dataVersion]);

  const load = useCallback(async () => {
    if (!projectId) { setItems([]); setTotal(0); return; }
    setLoading(true);
    try {
      const params = { project_id: projectId, current_status: "GRN_COMPLETED", page, page_size: pageSize, search: dSearch, sort };
      if (dSerialFrom) params.serial_from = dSerialFrom;
      if (dSerialTo) params.serial_to = dSerialTo;
      if (typeId !== "__all__") params.material_type_id = typeId;
      const reqId = ++loadSeq.current;   // latest-wins: stale responses never overwrite newer ones
      const { data } = await api.get("/materials", { params });
      if (reqId !== loadSeq.current) return;
      setItems(data.items || []); setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, [projectId, typeId, dSearch, dSerialFrom, dSerialTo, sort, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSel(new Set()); }, [projectId]);  // selection survives paging/filtering so "select all filtered" persists

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePage = () => setSel((s) => { const all = items.every((m) => s.has(m.id)); const n = new Set(s); items.forEach((m) => all ? n.delete(m.id) : n.add(m.id)); return n; });
  const allPageSel = items.length > 0 && items.every((m) => sel.has(m.id));
  const [selAllLoading, setSelAllLoading] = useState(false);
  const selectAllFiltered = async () => {
    setSelAllLoading(true);
    try {
      const ids = new Set(sel);
      let p = 1;
      for (;;) {
        const params = { project_id: projectId, current_status: "GRN_COMPLETED", page: p, page_size: 500, search: dSearch, sort };
        if (dSerialFrom) params.serial_from = dSerialFrom;
        if (dSerialTo) params.serial_to = dSerialTo;
        if (typeId !== "__all__") params.material_type_id = typeId;
        const { data } = await api.get("/materials", { params });
        (data.items || []).forEach((m) => ids.add(m.id));
        if (!data.has_more || ids.size >= 5000) break;
        p += 1;
      }
      setSel(ids);
      toast.success(`Selected ${ids.size} materials matching the current filter`);
    } catch { toast.error("Failed to select all filtered materials"); }
    finally { setSelAllLoading(false); }
  };
  const allocate = async () => {
    if (!fuId) { toast.error("Select a field user"); return; }
    if (submitting) return;
    setSubmitting(true);
    try { const { data } = await api.post("/allocate", { material_ids: [...sel], field_user_id: fuId, idempotency_key: uuidv4() });
      if (data.success) toast.success(`Allocated ${data.success}${data.failed ? `, ${data.failed} blocked` : ""}`);
      if (data.failed) {
        const firstMsg = (data.results || []).find((r) => !r.ok)?.message;
        toast.error(`${data.failed} not allocated${firstMsg ? `: ${firstMsg}` : ""}`, { duration: 7000 });
      }
      setOpen(false); setSel(new Set()); setFuId(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <PageHeader title="Field Allocation" subtitle="Assign GRN-completed materials to field users (single or bulk)" />
      <ProjectScopeBanner requireProject action="allocation" />
      {projectId && (
      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="w-full sm:w-48"><Label>Type</Label>
            <SearchableSelect testid="alloc-type-filter" value={typeId && typeId !== "__all__" ? typeId : ""} onChange={(v) => { setPage(1); setTypeId(v || "__all__"); }} options={types.map((t) => ({ value: t.id, label: t.type_name }))} placeholder="All Types" className="mt-1" /></div>
          <ClearableInput testid="alloc-search" value={search} onChange={(v) => { setPage(1); setSearch(v); }} placeholder="Search serial..." className="sm:max-w-xs" />
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1"><Label className="text-xs">Serial From</Label>
            <ClearableInput testid="alloc-serial-from" value={serialFrom} onChange={(v) => { setPage(1); setSerialFrom(v); }} placeholder="e.g. TESTMETER-101" className="mt-1" /></div>
          <div className="flex-1"><Label className="text-xs">Serial To</Label>
            <ClearableInput testid="alloc-serial-to" value={serialTo} onChange={(v) => { setPage(1); setSerialTo(v); }} placeholder="e.g. TESTMETER-120" className="mt-1" /></div>
          <div className="w-full sm:w-40"><Label className="text-xs">Sort by Serial</Label>
            <Select value={sort} onValueChange={(v) => { setPage(1); setSort(v); }}>
              <SelectTrigger data-testid="alloc-sort" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="serial_asc">Serial A → Z</SelectItem>
                <SelectItem value="serial_desc">Serial Z → A</SelectItem>
                <SelectItem value="recent">Most recent</SelectItem>
              </SelectContent>
            </Select></div>
          {(serialFrom || serialTo) && <Button variant="ghost" size="sm" data-testid="alloc-range-clear" onClick={() => { setPage(1); setSerialFrom(""); setSerialTo(""); }}>Clear range</Button>}
        </div>
      </div>
      )}
      {sel.size > 0 && <div className="mb-3 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5">
        <span className="text-sm font-medium text-blue-900" data-testid="alloc-selected-count">{sel.size} selected</span>
        <Button data-testid="alloc-action-btn" size="sm" onClick={() => { setFuId(""); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700"><UserPlus className="mr-1.5 h-4 w-4" /> Allocate</Button>
        <Button size="sm" variant="ghost" onClick={() => setSel(new Set())}>Clear</Button></div>}
      {allPageSel && total > items.length && sel.size < total && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800" data-testid="alloc-select-all-filtered">
          <span>All {items.length} on this page are selected.</span>
          <Button size="sm" variant="outline" className="border-blue-300 bg-white text-blue-700" disabled={selAllLoading} onClick={selectAllFiltered} data-testid="alloc-select-all-filtered-btn">
            {selAllLoading ? "Selecting…" : `Select all ${total} matching this filter`}
          </Button>
        </div>
      )}
      {total > items.length && sel.size >= total && (
        <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="alloc-all-filtered-selected">All {total} materials matching this filter are selected.</div>
      )}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left"><thead><tr className="border-b border-slate-200 bg-slate-100/80">
          <th className="px-3 py-3"><Checkbox data-testid="alloc-select-page" checked={allPageSel} onCheckedChange={togglePage} /></th><th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Serial</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Type</th><th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Make</th></tr></thead>
          <tbody data-testid="alloc-rows">
            {loading ? <tr><td colSpan={4} className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
              : !projectId ? <tr><td colSpan={4} className="py-10 text-center text-sm text-slate-400">Select a specific project from the top bar</td></tr>
              : items.length === 0 ? <tr><td colSpan={4} className="py-10 text-center text-sm text-slate-400">No available materials</td></tr>
              : items.map((m) => <tr key={m.id} className={sel.has(m.id) ? "bg-blue-50/50" : "hover:bg-slate-50"}>
                <td className="px-3 py-2.5"><Checkbox data-testid={`alloc-select-${m.id}`} checked={sel.has(m.id)} onCheckedChange={() => toggle(m.id)} /></td>
                <td className="px-4 py-2.5 font-mono text-xs font-medium">{m.material_number}</td>
                <td className="px-4 py-2.5 text-sm"><Badge variant="secondary">{m.type_name}</Badge></td>
                <td className="px-4 py-2.5 text-sm text-slate-600">{m.make_name}</td></tr>)}
          </tbody></table>
      </div>
      <div className="mt-3 flex items-center justify-between text-sm text-slate-500"><span>{total} available</span>
        <div className="flex items-center gap-2"><Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span>Page {page}/{totalPages}</span><Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button></div></div>
      <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Allocate {sel.size} to Field User</DialogTitle>
          <DialogDescription>Assign the selected GRN-completed materials to a field user for this project.</DialogDescription>
        </DialogHeader>
        <div><Label>Field User *</Label><SearchableSelect testid="alloc-fu-select" value={fuId} onChange={setFuId} options={fieldUsers.map((u) => ({ value: u.id, label: u.full_name, sub: u.email || u.mobile }))} placeholder="Select field user" className="mt-1" />
          {fieldUsers.length === 0 && <p className="mt-2 text-xs text-amber-600" data-testid="alloc-no-fu-hint">No field users are assigned to this project. In User Master enable the "Field User" toggle, then assign them to this project under Assignments.</p>}
        </div>
        {fuId && capacity.length > 0 && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2.5" data-testid="alloc-capacity">
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Current load — this project</div>
            <div className="space-y-1">
              {capacity.map((c) => (
                <div key={c.material_type_id} className="flex items-center justify-between text-xs" data-testid={`alloc-cap-${c.material_type_id}`}>
                  <span className="text-slate-700">{c.type_name}</span>
                  <span className="flex items-center gap-2">
                    {c.qty_enabled
                      ? <span className={c.remaining === 0 ? "font-semibold text-red-600" : c.remaining <= 2 ? "font-medium text-amber-600" : "text-slate-600"}>{c.held_count}/{c.max_qty} held · {c.remaining} left</span>
                      : <span className="text-slate-500">{c.held_count} held</span>}
                    {c.days_enabled && c.has_aged && <span className="rounded bg-red-100 px-1.5 py-0.5 font-semibold text-red-700">over {c.max_days}d — blocked</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" disabled={submitting} onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="alloc-confirm-btn" disabled={submitting} onClick={allocate} className="bg-blue-600 hover:bg-blue-700">{submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Allocate</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
